/* ═══════════ HAR SYNC TAB (Admin ▸ Data) ═══════════
   RWB-only bridge: add-only import from the SpecForma_HAR_Sync workbook the
   Power Automate flow maintains. Visible only to HAR_SYNC_EMAIL on desktop
   (gated in AdminPage). TWO-STEP: Preview computes a read-only plan; rows can
   be deselected (click toggles, ctrl+click toggles, shift+click ranges) and
   ONLY the selected rows are written on Apply. Startup sync just notifies
   unless the auto-apply pref is on. */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  getHarSyncSource,
  setHarSyncSource,
  getHarAutoApply,
  setHarAutoApply,
  computeHarSyncPlan,
  applyHarSyncPlan,
  getPDriveRoot,
  setPDriveRoot,
  listPDriveFolders,
  computePDrivePlan,
  applyPDriveClose,
  type HarSyncPlan,
  type HarSyncSummary,
  type PDrivePlan,
  type PDriveCloseResult,
} from '../services/harSyncService';
import { ProjectService } from '../services/projectService';
import { hasPermission } from '../../shared/permissions';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { Checkbox } from '../components/ui/checkbox';
import { rem } from '../lib/rem';

const inputStyle: React.CSSProperties = {
  padding: `${rem(7)} ${rem(10)}`,
  borderRadius: rem(5),
  border: '1px solid var(--bd)',
  background: 'var(--ib)',
  color: 'var(--tx)',
  fontSize: 'var(--font-size-base)',
  width: '100%',
};
const btnPrimary: React.CSSProperties = {
  padding: `${rem(7)} var(--space-4)`,
  borderRadius: 'var(--radius-md)',
  border: 'none',
  background: 'var(--ac)',
  color: 'var(--color-text-inverse)',
  fontSize: 'calc(var(--font-size-base) * 12 / 13)',
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};
const btnSecondary: React.CSSProperties = {
  padding: `${rem(7)} var(--space-4)`,
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--bd)',
  background: 'var(--sf)',
  color: 'var(--tx)',
  fontSize: 'calc(var(--font-size-base) * 12 / 13)',
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};
const linkBtn: React.CSSProperties = {
  border: 'none',
  background: 'transparent',
  color: 'var(--ac)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 600,
  cursor: 'pointer',
  padding: `${rem(2)} ${rem(6)}`,
};

type Group = 'Projects' | 'Deliverables' | 'Assignments' | 'Forecasts';
interface PlanRow {
  id: string;
  group: Group;
  label: string;
  sub: string;
}

/** Flatten the plan into selectable rows. Forecasts select at the
 *  member+project (weekly) level — the day-cells follow the pair. */
function planToRows(plan: HarSyncPlan): PlanRow[] {
  const rows: PlanRow[] = [];
  for (const p of plan.newProjects) {
    rows.push({
      id: `P|${p.projectNo}`,
      group: 'Projects',
      label: `${p.projectNo} — ${p.name}`,
      sub: p.client || '',
    });
  }
  for (const d of plan.newDeliverables) {
    rows.push({
      id: `D|${d.projectNo}|${d.name.toLowerCase()}`,
      group: 'Deliverables',
      label: `${d.projectNo} · ${d.name}`,
      sub: d.dueDate ? `due ${d.dueDate}` : 'no date',
    });
  }
  for (const a of plan.newAssignments) {
    rows.push({
      id: `A|${a.projectNo}|${a.memberId}`,
      group: 'Assignments',
      label: `${a.memberName} → ${a.projectNo}`,
      sub: a.role,
    });
  }
  for (const f of plan.forecastPairsList) {
    rows.push({
      id: `F|${f.projectNo}|${f.memberId}`,
      group: 'Forecasts',
      label: `${f.memberName} → ${f.projectNo}`,
      sub: `${f.hours}h · week of ${plan.weekStart}`,
    });
  }
  return rows;
}

/** Filter the plan down to the selected row ids before Apply. */
function filterPlan(plan: HarSyncPlan, selected: Set<string>): HarSyncPlan {
  const keptPairs = plan.forecastPairsList.filter((f) =>
    selected.has(`F|${f.projectNo}|${f.memberId}`)
  );
  const keptPairSet = new Set(keptPairs.map((f) => `${f.projectNo}|${f.memberId}`));
  return {
    ...plan,
    newProjects: plan.newProjects.filter((p) => selected.has(`P|${p.projectNo}`)),
    newDeliverables: plan.newDeliverables.filter((d) =>
      selected.has(`D|${d.projectNo}|${d.name.toLowerCase()}`)
    ),
    newAssignments: plan.newAssignments.filter((a) =>
      selected.has(`A|${a.projectNo}|${a.memberId}`)
    ),
    forecastPairsList: keptPairs,
    forecastCells: plan.forecastCells.filter((c) =>
      keptPairSet.has(`${c.projectNo}|${c.memberId}`)
    ),
  };
}

interface HarSyncTabProps {
  toast: (msg: string, isError?: boolean) => void;
  userPermissions?: string[];
}

export function HarSyncTab({ toast, userPermissions }: HarSyncTabProps) {
  const [source, setSource] = useState('');
  const [autoApply, setAuto] = useState(false);
  // Prefs live in a file (via IPC) — hydrate once on mount.
  useEffect(() => {
    void (async () => {
      setSource(await getHarSyncSource());
      setAuto(await getHarAutoApply());
    })();
  }, []);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [plan, setPlan] = useState<HarSyncPlan | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<HarSyncSummary | null>(null);
  const [error, setError] = useState('');
  const anchorRef = useRef<number | null>(null);

  const rows = useMemo(() => (plan ? planToRows(plan) : []), [plan]);

  const browse = async () => {
    const p = await window.api.openHarWorkbookDialog();
    if (p) {
      setSource(p);
      await setHarSyncSource(p);
    }
  };

  const preview = async () => {
    const s = source.trim();
    await setHarSyncSource(s);
    if (!s) {
      toast('Set the workbook path or share link first', true);
      return;
    }
    setBusy(true);
    setError('');
    setResult(null);
    setPlan(null);
    try {
      const p = await computeHarSyncPlan(setProgress);
      if (p) {
        setPlan(p);
        setSelected(new Set(planToRows(p).map((r) => r.id))); // everything in by default
        anchorRef.current = null;
        if (p.total === 0) toast('Already up to date — nothing to add');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      toast('HAR preview failed', true);
    } finally {
      setBusy(false);
      setProgress('');
    }
  };

  /** Explorer-style: click / ctrl+click toggles the row; shift+click applies
   *  the clicked row's NEW state to the whole range from the last click. */
  const onRowClick = (e: React.MouseEvent, idx: number) => {
    const next = new Set(selected);
    const id = rows[idx].id;
    const newState = !next.has(id);
    if (e.shiftKey && anchorRef.current !== null) {
      const [a, b] = [Math.min(anchorRef.current, idx), Math.max(anchorRef.current, idx)];
      for (let i = a; i <= b; i++) {
        if (newState) next.add(rows[i].id);
        else next.delete(rows[i].id);
      }
    } else {
      if (newState) next.add(id);
      else next.delete(id);
      anchorRef.current = idx;
    }
    setSelected(next);
  };

  const setGroup = (group: Group | null, on: boolean) => {
    const next = new Set(selected);
    for (const r of rows) {
      if (group && r.group !== group) continue;
      if (on) next.add(r.id);
      else next.delete(r.id);
    }
    setSelected(next);
  };

  const apply = async () => {
    if (!plan) return;
    setBusy(true);
    setError('');
    try {
      const filtered = filterPlan(plan, selected);
      const res = await applyHarSyncPlan(filtered, setProgress);
      setResult(res);
      setPlan(null);
      toast(
        `HAR sync applied: +${res.projectsAdded} projects, +${res.deliverablesAdded} deliverables, ` +
          `+${res.assignmentsAdded} assignments, ${res.forecastCellsSet} forecast cells`
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      toast('HAR apply failed', true);
    } finally {
      setBusy(false);
      setProgress('');
    }
  };

  const selCount = rows.filter((r) => selected.has(r.id)).length;
  const groups: Group[] = ['Projects', 'Deliverables', 'Assignments', 'Forecasts'];

  // ─── P drive check: a SEPARATE, manual-only function — reads folder names
  // only, never auto-run, never writes to the drive. ───
  const [pdriveRoot, setPdriveRootInput] = useState('');
  useEffect(() => {
    void (async () => setPdriveRootInput(await getPDriveRoot()))();
  }, []);
  const [pdriveBusy, setPdriveBusy] = useState(false);
  const [pdriveStage, setPdriveStage] = useState<'idle' | 'reading'>('idle');
  const [pdriveError, setPdriveError] = useState('');
  const [pdrivePlan, setPdrivePlan] = useState<PDrivePlan | null>(null);
  const [pdriveSelected, setPdriveSelected] = useState<Set<string>>(new Set());
  const [pdriveResult, setPdriveResult] = useState<PDriveCloseResult | null>(null);
  const [pdriveApplyBusy, setPdriveApplyBusy] = useState(false);
  const [pdriveConfirmOpen, setPdriveConfirmOpen] = useState(false);

  const canCloseProjects = hasPermission(userPermissions, 'projects.archive');

  const checkPDrive = async () => {
    const root = pdriveRoot.trim();
    await setPDriveRoot(root);
    if (!root) {
      toast('Set the P drive path first', true);
      return;
    }
    setPdriveBusy(true);
    setPdriveStage('reading');
    setPdriveError('');
    setPdrivePlan(null);
    setPdriveResult(null);
    try {
      const listed = await listPDriveFolders(root);
      if (!listed.ok) {
        // listed.message is already a specific, plain-English reason —
        // verify-unreachable, forbidden, or the filesystem error — never a
        // generic string that would mask which of those it was.
        setPdriveError(listed.message);
        return;
      }
      const projects = await ProjectService.list();
      const plan = computePDrivePlan(listed.folders, projects);
      setPdrivePlan(plan);
      setPdriveSelected(new Set(plan.toClose.map((p) => p.id)));
    } catch (err) {
      setPdriveError(err instanceof Error ? err.message : String(err));
    } finally {
      setPdriveBusy(false);
      setPdriveStage('idle');
    }
  };

  const togglePDriveRow = (id: string) => {
    setPdriveSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const setPDriveSelectAll = (on: boolean) => {
    if (!pdrivePlan) return;
    setPdriveSelected(on ? new Set(pdrivePlan.toClose.map((p) => p.id)) : new Set());
  };

  const closePDriveProjects = async () => {
    if (!pdrivePlan) return;
    const toClose = pdrivePlan.toClose.filter((p) => pdriveSelected.has(p.id));
    if (toClose.length === 0) return;
    setPdriveApplyBusy(true);
    try {
      const res = await applyPDriveClose(toClose);
      setPdriveResult(res);
      setPdrivePlan(null);
      setPdriveConfirmOpen(false);
      toast(
        `P drive check: closed ${res.closed}${res.failed.length ? `, ${res.failed.length} failed` : ''}`,
        res.failed.length > 0
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast(`P drive close failed: ${msg}`, true);
    } finally {
      setPdriveApplyBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: rem(760) }}>
      <h3
        style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, margin: '0 0 var(--space-1)' }}
      >
        HAR Sync
      </h3>
      <p
        style={{
          fontSize: 'var(--font-size-base)',
          color: 'var(--tm)',
          margin: `0 0 var(--space-4)`,
          lineHeight: 1.5,
        }}
      >
        Add-only import from the SpecForma_HAR_Sync workbook (RWB bridge). Preview shows every
        change first — nothing is written until you Apply, and you can deselect any row (click to
        toggle, Shift+click for a range). It never updates or deletes existing records.
      </p>

      <label
        style={{
          display: 'block',
          fontSize: 'calc(var(--font-size-base) * 12 / 13)',
          fontWeight: 600,
          marginBottom: 'var(--space-1)',
        }}
      >
        Workbook source
      </label>
      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
        <input
          style={inputStyle}
          value={source}
          placeholder="Local path to SpecForma_HAR_Sync.xlsx or a OneDrive share link"
          onChange={(e) => setSource(e.target.value)}
          onBlur={() => void setHarSyncSource(source.trim())}
          disabled={busy}
        />
        <button style={btnSecondary} onClick={browse} disabled={busy}>
          Browse…
        </button>
        <button
          style={{ ...btnPrimary, opacity: busy ? 0.6 : 1 }}
          onClick={preview}
          disabled={busy}
        >
          {busy ? 'Working…' : 'Preview Changes'}
        </button>
      </div>
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: rem(6),
          fontSize: 'calc(var(--font-size-base) * 12 / 13)',
          color: 'var(--tm)',
          margin: `var(--space-2) 0 0`,
          cursor: 'pointer',
          width: 'fit-content',
        }}
      >
        <input
          type="checkbox"
          checked={autoApply}
          onChange={(e) => {
            setAuto(e.target.checked);
            void setHarAutoApply(e.target.checked);
          }}
        />
        Apply automatically at sign-in (skips this review — leave off until you trust it)
      </label>

      {busy && progress && (
        <p
          style={{
            fontSize: 'var(--font-size-base)',
            color: 'var(--tm)',
            marginTop: 'var(--space-4)',
          }}
        >
          {progress}
        </p>
      )}
      {error && (
        <p
          style={{
            fontSize: 'var(--font-size-base)',
            color: 'var(--dg)',
            marginTop: 'var(--space-4)',
          }}
        >
          {error}
        </p>
      )}

      {plan && plan.total > 0 && (
        <div style={{ marginTop: rem(20) }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 'var(--space-2)',
            }}
          >
            <div style={{ fontSize: 'var(--font-size-base)', fontWeight: 700 }}>
              Pending changes — week of {plan.weekStart || '—'}
            </div>
            <div>
              <button style={linkBtn} onClick={() => setGroup(null, true)}>
                Select all
              </button>
              <button style={linkBtn} onClick={() => setGroup(null, false)}>
                Deselect all
              </button>
            </div>
          </div>

          <div
            style={{
              border: '1px solid var(--bd)',
              borderRadius: 'var(--radius-md)',
              maxHeight: rem(360),
              overflowY: 'auto',
              userSelect: 'none',
            }}
          >
            {groups.map((g) => {
              const groupRows = rows.map((r, i) => ({ r, i })).filter(({ r }) => r.group === g);
              if (groupRows.length === 0) return null;
              const groupSel = groupRows.filter(({ r }) => selected.has(r.id)).length;
              return (
                <div key={g}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: `${rem(6)} var(--space-3)`,
                      background: 'var(--sa)',
                      borderBottom: '1px solid var(--bd)',
                      position: 'sticky',
                      top: 0,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '.04em',
                        color: 'var(--tm)',
                      }}
                    >
                      {g} — {groupSel}/{groupRows.length} selected
                    </span>
                    <span>
                      <button style={linkBtn} onClick={() => setGroup(g, true)}>
                        All
                      </button>
                      <button style={linkBtn} onClick={() => setGroup(g, false)}>
                        None
                      </button>
                    </span>
                  </div>
                  {groupRows.map(({ r, i }) => {
                    const on = selected.has(r.id);
                    return (
                      <div
                        key={r.id}
                        onClick={(e) => onRowClick(e, i)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: rem(10),
                          padding: `${rem(5)} var(--space-3)`,
                          fontSize: 'var(--font-size-base)',
                          cursor: 'pointer',
                          opacity: on ? 1 : 0.45,
                          background: on ? 'transparent' : 'var(--sa)',
                          borderBottom: '1px solid var(--bd)',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={on}
                          readOnly
                          style={{ pointerEvents: 'none', flexShrink: 0 }}
                        />
                        <span
                          style={{
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            textDecoration: on ? 'none' : 'line-through',
                          }}
                        >
                          {r.label}
                        </span>
                        <span
                          style={{
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--tm)',
                            flexShrink: 0,
                          }}
                        >
                          {r.sub}
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {(plan.skippedNoMember.length > 0 ||
            plan.skippedNoEmail.length > 0 ||
            plan.skippedTbd > 0) && (
            <p
              style={{
                fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                color: 'var(--tm)',
                margin: `var(--space-2) 0 0`,
              }}
            >
              Skipped automatically:
              {plan.skippedTbd > 0 ? ` ${plan.skippedTbd} TBD deliverables ·` : ''}
              {plan.skippedNoMember.length > 0
                ? ` not in SpecForma: ${plan.skippedNoMember.join(', ')} ·`
                : ''}
              {plan.skippedNoEmail.length > 0
                ? ` no email in PEOPLE_MAP: ${plan.skippedNoEmail.join(', ')}`
                : ''}
            </p>
          )}

          <div
            style={{
              display: 'flex',
              gap: 'var(--space-2)',
              marginTop: 'var(--space-3)',
              alignItems: 'center',
            }}
          >
            <button
              style={{ ...btnPrimary, opacity: busy || selCount === 0 ? 0.6 : 1 }}
              onClick={apply}
              disabled={busy || selCount === 0}
            >
              Apply {selCount} of {rows.length} changes
            </button>
            <button style={btnSecondary} onClick={() => setPlan(null)} disabled={busy}>
              Cancel
            </button>
            <span style={{ fontSize: 'calc(var(--font-size-base) * 12 / 13)', color: 'var(--tm)' }}>
              Deselected rows are skipped; items belonging to a deselected project are skipped
              automatically.
            </span>
          </div>
        </div>
      )}

      {result && (
        <div style={{ marginTop: rem(20) }}>
          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            {[
              { label: 'Projects added', value: result.projectsAdded },
              { label: 'Deliverables added', value: result.deliverablesAdded },
              { label: 'Assignments added', value: result.assignmentsAdded },
              { label: 'Forecast cells written', value: result.forecastCellsSet },
            ].map((c) => (
              <div
                key={c.label}
                style={{
                  padding: `${rem(10)} ${rem(14)}`,
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--bd)',
                  background: 'var(--sa)',
                  minWidth: rem(120),
                }}
              >
                <div style={{ fontSize: rem(20), fontWeight: 700 }}>{c.value}</div>
                <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>{c.label}</div>
              </div>
            ))}
          </div>
          {result.errors.length > 0 && (
            <p
              style={{
                fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                color: 'var(--dg)',
                margin: `var(--space-2) 0 0`,
              }}
            >
              <strong>Errors:</strong> {result.errors.join(' · ')}
            </p>
          )}
        </div>
      )}

      {/* ═══ P drive check — separate, manual-only function ═══ */}
      <div
        style={{
          marginTop: rem(40),
          paddingTop: rem(24),
          borderTop: '1px solid var(--bd)',
        }}
      >
        <h3
          style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, margin: '0 0 var(--space-1)' }}
        >
          P drive check
        </h3>
        <p
          style={{
            fontSize: 'var(--font-size-base)',
            color: 'var(--tm)',
            margin: `0 0 var(--space-4)`,
            lineHeight: 1.5,
          }}
        >
          Reads folder NAMES only from the path below — it never opens, renames, moves, or changes
          anything on the P drive. Manual only: this never runs automatically. Compares against
          active SpecForma projects so you can review and close the ones with no matching folder.
        </p>

        <label
          style={{
            display: 'block',
            fontSize: 'calc(var(--font-size-base) * 12 / 13)',
            fontWeight: 600,
            marginBottom: 'var(--space-1)',
          }}
        >
          P drive path
        </label>
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
          <input
            style={inputStyle}
            value={pdriveRoot}
            placeholder="P:\"
            onChange={(e) => setPdriveRootInput(e.target.value)}
            onBlur={() => void setPDriveRoot(pdriveRoot.trim())}
            disabled={pdriveBusy}
          />
          <button
            style={{ ...btnPrimary, opacity: pdriveBusy ? 0.6 : 1 }}
            onClick={checkPDrive}
            disabled={pdriveBusy}
          >
            {pdriveBusy && pdriveStage === 'reading' ? 'Reading…' : 'Check P drive'}
          </button>
        </div>

        {pdriveError && (
          <p
            style={{
              fontSize: 'var(--font-size-base)',
              color: 'var(--dg)',
              marginTop: 'var(--space-4)',
            }}
          >
            {pdriveError}
          </p>
        )}

        {pdrivePlan?.blocked && (
          <p
            style={{
              fontSize: 'var(--font-size-base)',
              color: 'var(--tm)',
              marginTop: 'var(--space-4)',
            }}
          >
            {pdrivePlan.blocked}
          </p>
        )}

        {pdrivePlan && !pdrivePlan.blocked && (
          <div style={{ marginTop: rem(20) }}>
            <p
              style={{
                fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                color: 'var(--tm)',
                margin: `0 0 var(--space-3)`,
              }}
            >
              {pdrivePlan.folderCount} folders read · {pdrivePlan.recognisedCount} project numbers
              recognised · {pdrivePlan.unrecognisedCount} unrecognised folder names
            </p>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 'var(--space-2)',
              }}
            >
              <div style={{ fontSize: 'var(--font-size-base)', fontWeight: 700 }}>
                Active in SpecForma, not on the P drive
              </div>
              {pdrivePlan.toClose.length > 0 && (
                <div>
                  <button style={linkBtn} onClick={() => setPDriveSelectAll(true)}>
                    Select all
                  </button>
                  <button style={linkBtn} onClick={() => setPDriveSelectAll(false)}>
                    Deselect all
                  </button>
                </div>
              )}
            </div>

            {pdrivePlan.toClose.length === 0 ? (
              <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--tm)' }}>
                Nothing to close — every active project has a matching P drive folder.
              </p>
            ) : (
              <>
                <div
                  style={{
                    border: '1px solid var(--bd)',
                    borderRadius: 'var(--radius-md)',
                    maxHeight: rem(300),
                    overflowY: 'auto',
                    userSelect: 'none',
                  }}
                >
                  {pdrivePlan.toClose.map((p) => {
                    const on = pdriveSelected.has(p.id);
                    return (
                      <div
                        key={p.id}
                        onClick={() => togglePDriveRow(p.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: rem(10),
                          padding: `${rem(5)} var(--space-3)`,
                          fontSize: 'var(--font-size-base)',
                          cursor: 'pointer',
                          opacity: on ? 1 : 0.45,
                          background: on ? 'transparent' : 'var(--sa)',
                          borderBottom: '1px solid var(--bd)',
                        }}
                      >
                        <Checkbox
                          checked={on}
                          onCheckedChange={() => togglePDriveRow(p.id)}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`${p.projectNumber || '—'} — ${p.name}`}
                        />
                        <span style={{ flexShrink: 0, fontWeight: 600 }}>
                          {p.projectNumber || '—'}
                        </span>
                        <span
                          style={{
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {p.name}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <div
                  style={{
                    display: 'flex',
                    gap: 'var(--space-2)',
                    marginTop: 'var(--space-3)',
                    alignItems: 'center',
                  }}
                >
                  <button
                    style={{
                      ...btnPrimary,
                      opacity:
                        pdriveApplyBusy || pdriveSelected.size === 0 || !canCloseProjects ? 0.6 : 1,
                    }}
                    onClick={() => setPdriveConfirmOpen(true)}
                    disabled={pdriveApplyBusy || pdriveSelected.size === 0 || !canCloseProjects}
                  >
                    Close {pdriveSelected.size} projects
                  </button>
                  {!canCloseProjects && (
                    <span
                      style={{
                        fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                        color: 'var(--tm)',
                      }}
                    >
                      Requires the Close/Reopen project permission.
                    </span>
                  )}
                </div>
              </>
            )}

            <div
              style={{
                fontSize: 'var(--font-size-base)',
                fontWeight: 700,
                margin: `var(--space-5) 0 var(--space-2)`,
              }}
            >
              On the P drive, not in SpecForma
            </div>
            {pdrivePlan.notInSpecForma.length === 0 ? (
              <p style={{ fontSize: 'var(--font-size-base)', color: 'var(--tm)' }}>
                None — every recognised folder number has a SpecForma project.
              </p>
            ) : (
              <div
                style={{
                  border: '1px solid var(--bd)',
                  borderRadius: 'var(--radius-md)',
                  maxHeight: rem(240),
                  overflowY: 'auto',
                }}
              >
                {pdrivePlan.notInSpecForma.map((f) => (
                  <div
                    key={f.projectNo}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: rem(10),
                      padding: `${rem(5)} var(--space-3)`,
                      fontSize: 'var(--font-size-base)',
                      borderBottom: '1px solid var(--bd)',
                    }}
                  >
                    <span style={{ flexShrink: 0, fontWeight: 600 }}>{f.projectNo}</span>
                    <span
                      style={{
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: 'var(--tm)',
                      }}
                    >
                      {f.folder}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {pdriveResult && (
          <div style={{ marginTop: rem(20) }}>
            <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 700 }}>
              {pdriveResult.closed} closed
              {pdriveResult.failed.length > 0 ? `, ${pdriveResult.failed.length} failed` : ''}
            </p>
            {pdriveResult.failed.length > 0 && (
              <p
                style={{
                  fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                  color: 'var(--dg)',
                  margin: `var(--space-2) 0 0`,
                }}
              >
                <strong>Failed:</strong> {pdriveResult.failed.map((f) => f.projectNo).join(', ')}
              </p>
            )}
          </div>
        )}

        <ConfirmDialog
          open={pdriveConfirmOpen}
          onOpenChange={(o) => !o && !pdriveApplyBusy && setPdriveConfirmOpen(false)}
          title={`Close ${pdriveSelected.size} project${pdriveSelected.size === 1 ? '' : 's'}?`}
          description="Every field, phase, deliverable, team assignment, spec, budget, time entry, forecast and invoice on these projects becomes read-only. Only Reopen can change it."
          confirmLabel={`Close ${pdriveSelected.size} projects`}
          destructive
          busy={pdriveApplyBusy}
          onConfirm={closePDriveProjects}
        />
      </div>
    </div>
  );
}
